import Decimal from "decimal.js";
import { token } from "./contants";

import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { Affiliate } from "../types/Affiliate";
import { AffiliateData } from "../types/AffiliateData";
import { Sale } from "../types/Sale";

export function generateRandomCode(): string {
  const charset = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  let code = "";

  for (let i = 0; i < 8; i++) {
    const randomIndex = Math.floor(Math.random() * charset.length);
    code += charset[randomIndex];
  }

  return code;
}

/**
 * Returns formatted amount with decimal point representaion
 */
export const formatBalance = (
  amount: Decimal | number | string,
  decimals = token.decimals
) => {
  if (amount === undefined || amount === null) {
    console.error("Error: amount is undefined or null");
    return "0"; // Default fallback value
  }

  const decimalAmount = new Decimal(amount); // Ensure amount is a Decimal
  return formatNum(
    decimalAmount.div(Decimal.pow(10, decimals)).toString(),
    decimals
  );
};
export const formatNum = (amount: string, decimals = token.decimals) => {
  const formattedNum = Number(amount).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  });
  if (formattedNum === "NaN") {
    return "--";
  }
  return formattedNum;
};
export function shortenAddress(address: string, chars = 4): string {
  if (!address || address.length <= chars * 2 + 2) {
    return address; // Return the original address if it's too short to shorten
  }
  const prefix = address.slice(0, chars + 2); // First `chars` + "0x"
  const suffix = address.slice(-chars); // Last `chars`
  return `${prefix}...${suffix}`;
}

/**
 * Copies the given text to the clipboard.
 * @param text - The text to be copied to the clipboard.
 * @returns A Promise that resolves when the text is successfully copied, or rejects with an error.
 */
export const copyToClipboard = async (text: string) => {
  if (
    navigator.clipboard &&
    typeof navigator.clipboard.writeText === "function"
  ) {
    try {
      await navigator.clipboard.writeText(text);
      console.log("Text copied to clipboard:", text);
      return true;
    } catch (error) {
      console.error("Failed to copy text to clipboard:", error);
      return false;
    }
  } else {
    // Fallback for browsers without clipboard API support
    try {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed"; // Prevent scrolling to bottom
      textArea.style.left = "-9999px"; // Hide the textarea
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      console.log("Text copied to clipboard (fallback):", text);
      return true;
    } catch (error) {
      console.error("Fallback failed to copy text to clipboard:", error);
      return false;
    }
  }
};

export const getTimeZoneDifSeconds = () => {
  let koreanTimeForWard = 9 * 60 * 60;
  let difTimeSeconds = koreanTimeForWard + new Date().getTimezoneOffset() * 60;
  return difTimeSeconds;
  //positive forward in minutes
}; // adjust path if needed

export function exportAffiliatesToExcel(
  affiliates: Affiliate[],
  affiliateDataMap: Map<string, AffiliateData>
) {
  // Prepare data: map Affiliates into plain objects
  const data = affiliates.map((a) => ({
    User: a.user,
    Revoked: a.revoked ? "Yes" : "No",
    ProfitPercent: a.profitPercent,
    TokensSold: formatBalance(a.tokensSold,token.decimals),
    USDTProfit: formatBalance(a.usdtProfit, 6),
    NativeProfit: formatBalance(a.nativeProfit, 18),
    USDTWithdrawn: formatBalance(a.usdtWithdrawn, 6),
    NativeWithdrawn: formatBalance(a.nativeWithdrawn, 18),
    Code: a.code,
    Note: affiliateDataMap.get(a.user)?.note ?? "",
  }));

  // Create a worksheet
  const worksheet = XLSX.utils.json_to_sheet(data);

  // Create a new workbook
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Affiliates");

  // Write the workbook to a Blob
  const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });

  // Save the Blob
  const fileData = new Blob([excelBuffer], {
    type: "application/octet-stream",
  });
  saveAs(fileData, "affiliates.xlsx");
}

export function exportSalesToExcel(sales: Sale[]) {
  // Prepare data: map Affiliates into plain objects

  const data = sales.map((sale) => ({
    User: sale.user,
    InvETH: sale.isUsdtPurchase ? 0 : formatBalance(sale.investment, 18),
    InvUSDT: sale.isUsdtPurchase ? formatBalance(sale.investment, 6) : 0,
    // Investment: formatBalance(sale.investment, sale.isUsdtPurchase ? 6 : 18),
    IsUSDTPurchase: sale.isUsdtPurchase ? "Yes" : "No",
    TokensTransferred: formatBalance(sale.tokensTransferred, token.decimals),
    ProfitETH: sale.isUsdtPurchase
      ? 0
      : formatBalance(sale.profit, sale.isUsdtPurchase ? 6 : 18),
    ProfitUSDT: sale.isUsdtPurchase
      ? formatBalance(sale.profit, sale.isUsdtPurchase ? 6 : 18)
      : 0,
    Referrer : sale.code,
    PurchaseTime: sale.purchaseTime.toLocaleString(),
  }));

  // Create a worksheet
  const worksheet = XLSX.utils.json_to_sheet(data);

  // Create a new workbook
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Sales");

  // Write the workbook to a Blob
  const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });

  // Save the Blob
  const fileData = new Blob([excelBuffer], {
    type: "application/octet-stream",
  });
  saveAs(fileData, "sales.xlsx");
}
